// 같은 출생 조건으로 산 두 인생(이전 / 이번)을 극적으로 비교하는 계산. 진행 기록만 읽고 원인을 단정하지 않는다.
// 비교표 · 가장 크게 달라진 것 · 순자산 겹쳐 보기 · 나란히 보는 사건 연표.
import { GROUPS, GROUP_LABEL } from './rules.js';
import { compareLives } from './summary.js';
import { jobTitle } from './engine.js';
import { todaySeries } from './economy.js';
import { buildLifeTimeline, manwonText } from './lifeResult.js';
import { buildResultCard } from './resultCard.js';

const MARRIAGE = { single: '미혼', married: '결혼 중', divorced: '이혼' };
const petsOf = (life) => life?.pet?.count ?? (life?.pet?.owned ? 1 : 0);
const signedManwon = (d) => (d === 0 ? '변화 없음' : `${d > 0 ? '+' : '-'}${manwonText(Math.abs(d))}`);
const signedNum = (d, unit = '') => (d === 0 ? '같음' : `${d > 0 ? '+' : ''}${Math.round(d * 10) / 10}${unit}`);

/** 한 인생의 비교용 값 */
export function lifeFacts(run) {
  const life = run.life, series = todaySeries(run);
  const peak = series.length ? series.reduce((b, p) => (p.netWorth > b.netWorth ? p : b), series[0]) : null;
  const last = series.at(-1) ?? null;
  const events = (run.eventHistory ?? []).filter((e) => e.outcome !== 'skipped');
  return {
    card: buildResultCard(run), endAge: Math.max(0, (run.age ?? 1) - 1),
    jobName: run.job?.name ?? null, jobTitle: run.job ? jobTitle(run.job) : null, jobLevel: run.job?.level ?? 0,
    jobChanges: Array.isArray(run.jobHistory) ? run.jobHistory.length : 0,
    netWorth: life?.economy && series.length ? series.at(-1).netWorth : null, peak, income: last?.income ?? null, series,
    marriage: life?.marriage?.status ?? null, marriedAt: life?.marriage?.at ?? null, divorces: life?.marriage?.divorces ?? 0,
    children: life?.children?.length ?? 0, homes: life?.home?.count ?? 0, pets: petsOf(life), education: life?.education ?? null,
    stats: Object.fromEntries(GROUPS.map((g) => [g, Math.round(run.stats?.[g] ?? 0)])),
    success: events.filter((e) => e.outcome === 'success').length, failure: events.filter((e) => e.outcome === 'failure').length,
    declined: (run.eventHistory ?? []).filter((e) => e.outcome === 'skipped').length,
  };
}

const decisionLine = (items) => {
  const line = (items ?? []).find((text) => !text.startsWith('시간:'));
  return line ? line.replace(/^[^:]+:\s*/, '') : null;
};

/** 비교표 한 줄: tone 은 이번이 이전보다 좋아졌는지(up) 나빠졌는지(down)다. 좋고 나쁨이 없는 항목은 tone 이 'flat' 이다. */
const row = (label, prev, now, diffText, tone) => ({ label, prev, now, diff: diffText, tone });
const numRow = (label, a, b, fmt, unit) => (a == null || b == null ? null
  : row(label, fmt(a), fmt(b), unit === 'money' ? signedManwon(b - a) : signedNum(b - a, unit), b === a ? 'same' : b > a ? 'up' : 'down'));

export function buildDrama(run, previous, lives) {
  void lives;
  const cmp = compareLives(run, previous);
  if (!cmp) return null;
  const prev = lifeFacts(previous), cur = lifeFacts(run), diff = cmp.firstDifference;
  const forkAge = diff?.age ?? null;
  const fork = diff ? { age: diff.age, previous: decisionLine(diff.previous), current: decisionLine(diff.current) } : null;

  const rows = [
    numRow('마지막 순자산', prev.netWorth, cur.netWorth, manwonText, 'money'),
    prev.peak && cur.peak ? row('최고 순자산', `${manwonText(prev.peak.netWorth)} (${prev.peak.age}세)`, `${manwonText(cur.peak.netWorth)} (${cur.peak.age}세)`, signedManwon(cur.peak.netWorth - prev.peak.netWorth), cur.peak.netWorth === prev.peak.netWorth ? 'same' : cur.peak.netWorth > prev.peak.netWorth ? 'up' : 'down') : null,
    numRow('마지막 연 소득', prev.income, cur.income, manwonText, 'money'),
    row('직업', prev.jobName ?? '없음', cur.jobName ?? '없음', prev.jobName === cur.jobName ? '같음' : '달라짐', prev.jobName === cur.jobName ? 'same' : 'flat'),
    numRow('직급(단계)', prev.jobLevel, cur.jobLevel, (v) => `${v}단계`, '단계'),
    numRow('전직', prev.jobChanges, cur.jobChanges, (v) => `${v}번`, '번'),
    row('결혼', `${MARRIAGE[prev.marriage] ?? '기록 없음'}${prev.marriedAt != null && prev.marriage !== 'single' ? ` (${prev.marriedAt}세)` : ''}`, `${MARRIAGE[cur.marriage] ?? '기록 없음'}${cur.marriedAt != null && cur.marriage !== 'single' ? ` (${cur.marriedAt}세)` : ''}`, prev.marriage === cur.marriage && prev.marriedAt === cur.marriedAt ? '같음' : '달라짐', prev.marriage === cur.marriage && prev.marriedAt === cur.marriedAt ? 'same' : 'flat'),
    numRow('자녀', prev.children, cur.children, (v) => `${v}명`, '명'),
    numRow('집', prev.homes, cur.homes, (v) => `${v}채`, '채'),
    numRow('반려동물', prev.pets, cur.pets, (v) => `${v}마리`, '마리'),
    ...GROUPS.map((g) => numRow(GROUP_LABEL[g], prev.stats[g], cur.stats[g], (v) => `${v}`, '')),
    numRow('성공한 선택', prev.success, cur.success, (v) => `${v}회`, '회'),
    numRow('실패한 선택', prev.failure, cur.failure, (v) => `${v}회`, '회'),
    numRow('거절한 선택', prev.declined, cur.declined, (v) => `${v}회`, '회'),
  ].filter(Boolean);

  // 가장 크게 달라진 것 3개: 항목마다 중요도를 비슷한 크기로 맞춰 비교한다(순자산 2억 = 자녀 1명 = 능력치 15)
  const scored = [];
  const push = (label, a, b, text, weight) => { const d = b - a; if (d !== 0) scored.push({ label, prev: a, now: b, text, score: Math.abs(d) / weight, up: d > 0 }); };
  if (prev.netWorth != null && cur.netWorth != null) push('마지막 순자산', prev.netWorth, cur.netWorth, signedManwon(cur.netWorth - prev.netWorth), 20000);
  push('자녀', prev.children, cur.children, `${cur.children - prev.children > 0 ? '+' : ''}${cur.children - prev.children}명`, 1);
  push('집', prev.homes, cur.homes, `${cur.homes - prev.homes > 0 ? '+' : ''}${cur.homes - prev.homes}채`, 1);
  push('전직', prev.jobChanges, cur.jobChanges, `${cur.jobChanges - prev.jobChanges > 0 ? '+' : ''}${cur.jobChanges - prev.jobChanges}번`, 1.5);
  for (const g of GROUPS) push(GROUP_LABEL[g], prev.stats[g], cur.stats[g], signedNum(cur.stats[g] - prev.stats[g]), 15);
  if (prev.jobName !== cur.jobName) scored.push({ label: '직업', prev: prev.jobName, now: cur.jobName, text: `${prev.jobName ?? '없음'} → ${cur.jobName ?? '없음'}`, score: 1, up: null });
  const biggest = scored.sort((a, b) => b.score - a.score).slice(0, 3);

  // 사건 연표를 나이별로 나란히: 어느 한쪽이라도 큰 사건이 있던 해만
  const moments = (r) => buildLifeTimeline(r).filter((x) => (x.kind === 'life' || x.kind === 'scene') && !x.minor);
  const byAge = new Map();
  for (const x of moments(previous)) byAge.set(x.age, { age: x.age, prev: [...(byAge.get(x.age)?.prev ?? []), x.title], now: byAge.get(x.age)?.now ?? [] });
  for (const x of moments(run)) byAge.set(x.age, { age: x.age, prev: byAge.get(x.age)?.prev ?? [], now: [...(byAge.get(x.age)?.now ?? []), x.title] });
  const timeline = [...byAge.values()].sort((a, b) => a.age - b.age).map((m) => ({ ...m, same: JSON.stringify(m.prev) === JSON.stringify(m.now), fork: forkAge != null && m.age === forkAge }));

  const headline = forkAge == null ? '두 번 모두 같은 선택을 해서 같은 인생이 됐어요. 이번엔 다르게 골라 보세요.' : `${forkAge}세, 두 인생이 갈라졌어요`;
  return { sameRules: cmp.sameRules, forkAge, fork, headline, prev, cur, rows, biggest, timeline,
    chart: { prev: prev.series, cur: cur.series, forkAge } };
}
